// Components
import InvoicePaymentStatusChange from 'components/InvoicePaymentStatusChange';
import { fetchEntityListRequest } from '../MerchantOnboard/redux/merchantOnBoard.actions';
import {
    Popup,
    Table,
    TableFilter,
    ViewDownloadStatus,
} from '../../components';
import { ENTITY_TYPE } from '../../utils/constants/commonValues';
import DownloadFile from './DownloadFile';

// Actions

// Libraries
import {
    fetchInvoiceDataRequest,
    generateInvoiceRequest,
    generateInvoiceReportRequest,
    generateInvoiceReportError,
    generateInvoiceReportSuccess,
    updateInvoicePaymentStatusRequest,
    updateInvoicePaymentStatusSuccess,
    updateInvoicePaymentStatusError,
} from './redux/invoice.actions';

import { INVOICE_PAYMENT_STATUS, INVOICE_SETTLEMENT_TYPES } from './constant';
import React, { useMemo, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import moment from 'moment';

const filterFieldList = [
    {
        fieldType: 'month-picker',
        label: 'Month Range',
        name: 'monthRange',
        props: {
            lowerMonth: moment()
                .subtract(1, 'months')
                .format('M'),
            lowerYear: moment().format('YYYY'),
            upperMonth: moment()
                .subtract(1, 'months')
                .format('M'),
            upperYear: moment().format('YYYY'),
        },
    },
    {
        fieldType: 'search-select',
        label: 'Merchant Name',
        name: 'merchantId',
        props: {
            data: [],
            label: 'Select Merchant',
            labelField: 'displayValue',
            valueField: 'merchantId',
        },
    },
    {
        fieldType: 'search-select',
        label: 'Payment Status',
        name: 'paymentStatus',
        props: {
            data: [],
            label: 'Select Payment Status',
            labelField: 'paymentStatus',
            valueField: 'paymentStatus',
        },
    },
    {
        fieldType: 'search-select',
        label: 'Settlement Type',
        name: 'settlementType',
        props: {
            data: [],
            label: 'Select Settlement Type',
            labelField: 'settlementType',
            valueField: 'settlementType',
        },
    },
];

const setMerchantFilter = (fieldsList, underlyingMerchant, userLevel) => {
    fieldsList.forEach(element => {
        if (element.name === 'paymentStatus') {
            element.props.data = INVOICE_PAYMENT_STATUS.map(value => {
                return { paymentStatus: value };
            });
        }
        if (element.name === 'settlementType') {
            element.props.data = INVOICE_SETTLEMENT_TYPES.map(value => {
                return { settlementType: value };
            });
        }
    });

    if (underlyingMerchant.length && userLevel && userLevel !== 'MERCHANT') {
        fieldsList.forEach(element => {
            if (element.name === 'merchantId') {
                element.props.data = underlyingMerchant.map(merchant => {
                    return {
                        merchantName: merchant.name,
                        merchantId: merchant.entityCode,
                        displayValue:
                            `${merchant.name} ` + `(${merchant.entityCode})`,
                    };
                });
            }
        });
        return fieldsList;
    }

    return fieldsList.filter(item => item.name !== 'merchantId');
};

const ManageInvoiceActions = ({
    data,
    downloadRequest,
    handleOpenActionPopup,
    actions,
    fetchData,
    pageSize,
    pageIndex,
}) => {
    const [showFilter, setShowFilter] = useState(false);

    const originalData = data.original;
    const generateReport = () => {
        const req = {
            s3path: originalData.invoiceURL,
        };
        downloadRequest(req);
    };

    const handleOpenDataActionPopup = actionType => {
        setShowFilter(true);
    };

    const handleCloseDataActionPopup = () => {
        setShowFilter(false);
    };

    return (
        <div className="userAction  multiItem">
            <button className="download" onClick={generateReport} />
            <button
                className="view"
                id="view-merchant"
                onClick={() => handleOpenDataActionPopup('view')}
            />
            <Popup
                title="Invoice Data"
                showCloseIcon
                showOverlay
                showFilter={showFilter}
                closePopup={handleCloseDataActionPopup}
            >
                <InvoicePaymentStatusChange
                    data={originalData}
                    onUpdateStatusSuccess={() => {
                        handleCloseDataActionPopup();
                        fetchData({ pageSize, pageIndex });
                    }}
                />
            </Popup>
        </div>
    );
};

const InvoiceTable = ({
    actions,
    invoiceData,
    merchantData,
    auth,
    transactionData,
}) => {
    const {
        tableData,
        isLoading,
        pagination,
        generatedFile,
        error,
    } = invoiceData;
    const pageSizeOptions = [10, 20, 40, 60];
    const { entityList } = merchantData;
    const { userLevel } = auth.userDetails;

    const { activeReports } = transactionData || {};

    // We'll start our table without any data
    const [pageCount, setPageCount] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [pageIndex, setPageIndex] = useState(10);

    const [filterData, setFilterData] = useState({});
    const [isResetPage, setIsResetPage] = useState(false);
    const [showPopUp, setShowPopUp] = useState(false);
    const [showDownloadPopup, setShowDownloadPopup] = useState(false);

    const handleCloseViewDownloads = () => {
        setShowPopUp(false);
    };
    const handleViewDownloads = () => {
        setShowPopUp(true);
    };

    useEffect(() => {
        if (generatedFile.isSuccess) {
            setShowPopUp(true);
        }
        return () => {
            actions.generateInvoiceReportError({ error: {} });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generatedFile.isSuccess]);

    useEffect(() => {
        actions.fetchEntityListRequest(ENTITY_TYPE.MERCHANT);
    }, [actions, generatedFile.isSuccess]);

    const filters = setMerchantFilter(
        filterFieldList,
        entityList.merchant || [],
        userLevel
    );

    const fetchData = useCallback(
        (pageSize, pageIndex) => {
            const data = {
                pageSize: pageSize.pageSize,
                pageIndex: pageSize.pageIndex,
            };
            const payload = { ...data, ...filterData };
            actions.fetchInvoiceDataRequest(payload);

            setPageSize(pageSize.pageSize);
            setPageIndex(pageSize.pageIndex);
        },
        [actions, filterData]
    );

    const fetchFilterData = filterValues => {
        setFilterData(filterValues);
        // setIsResetPage(true);
    };

    useEffect(() => {
        setPageCount(Math.ceil(pagination.totalNumberOfRecords / pageSize));
    }, [pageSize, pagination.totalNumberOfRecords]);

    const columns = useMemo(
        () => [
            { header: 'Merchant ID', accessor: 'merchantId' },
            { header: 'Merchant Name', accessor: 'merchantName' },
            { header: 'Invoice Period', accessor: 'invoicePeriod' },
            { header: 'Invoice Date', accessor: 'invoiceDate' },
            { header: 'Invoice number', accessor: 'invoiceNumber' },
            { header: 'Settlement Type', accessor: 'settlementType' },
            { header: 'Payment Status', accessor: 'paymentStatus' },
            { header: 'Gross Amount', accessor: 'grossAmount' },
            { header: 'Total Fee', accessor: 'totalFee' },
            { header: 'Total GST', accessor: 'totalGst' },
            { header: 'Total Value', accessor: 'totalValue' },
            { header: 'Action', accessor: 'action' },
        ],
        []
    );

    return (
        <>
            <Table
                tableTitle="Invoice"
                filterActionTitle="View Downloads"
                onClickTableAction={handleViewDownloads}
                columns={columns}
                data={tableData}
                isFiltersEnable
                filterData={filterData}
                tableFilters={
                    <TableFilter
                        showOverlay
                        customFilterClass="rightSkewed"
                        fieldsList={filters}
                        filterData={filterData}
                        onSubmit={filterValues => {
                            const tempFilterValue = { ...filterValues };
                            fetchFilterData(tempFilterValue);
                        }}
                        onReset={resetValue => {
                            setFilterData(resetValue);
                            setIsResetPage(true);
                        }}
                    />
                }
                userActions={data => {
                    return (
                        <ManageInvoiceActions
                            data={data}
                            handleOpenActionPopup={handleViewDownloads}
                            downloadRequest={req => {
                                actions.generateInvoiceRequest(req);
                            }}
                            actions={actions}
                            invoiceData={invoiceData}
                            fetchData={fetchData}
                            pageSize={pageSize}
                            pageIndex={pageIndex}
                        />
                    );
                }}
                additionalActions={() => (
                    <DownloadFile
                        filterData={filterData}
                        tableData={tableData}
                        actions={actions}
                        showDownloadPopup={showDownloadPopup}
                        setShowDownloadPopup={setShowDownloadPopup}
                        generateData={generatedFile}
                        isDateRangeFieldRequired
                        dateRangeFieldName="monthRange"
                        userLevel={userLevel}
                    />
                )}
                isPaginationEnable
                loading={isLoading}
                error={error && error.errorMessage}
                fetchData={fetchData}
                numberOfRecord={pagination.totalNumberOfRecords}
                paginationLabel="Rows per page"
                pageSizeOptions={pageSizeOptions}
                pagePerView={3}
                pageCount={pageCount}
                isResetPage={isResetPage}
                afterResetPage={() => setIsResetPage(false)}
                tableActionPopup={
                    <Popup
                        title="Download Status"
                        showCloseIcon
                        showOverlay
                        showFilter={showPopUp}
                        closePopup={handleCloseViewDownloads}
                    >
                        <ViewDownloadStatus data={activeReports.reports} />
                    </Popup>
                }
            />
        </>
    );
};

ManageInvoiceActions.propTypes = {
    data: PropTypes.object,
    downloadRequest: PropTypes.func,
    handleOpenActionPopup: PropTypes.func,
    // actions: PropTypes.object,
};

InvoiceTable.propTypes = {
    actions: PropTypes.object,
    auth: PropTypes.object,
    data: PropTypes.object,
    manageBin: PropTypes.object,
    loader: PropTypes.object,
    invoiceData: PropTypes.object,
    merchantData: PropTypes.object,
    transactionData: PropTypes.object,
};

const mapStateToProps = ({
    auth,
    loader,
    actions,
    invoiceData,
    merchantData,
    transactionData,
}) => ({
    auth,
    loader,
    actions,
    invoiceData,
    merchantData,
    transactionData,
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators(
        {
            fetchInvoiceDataRequest,
            updateInvoicePaymentStatusRequest,
            updateInvoicePaymentStatusSuccess,
            updateInvoicePaymentStatusError,
            fetchEntityListRequest,
            generateInvoiceRequest,
            generateInvoiceReportRequest,
            generateInvoiceReportError,
            generateInvoiceReportSuccess,
        },
        dispatch
    ),
});
export default connect(mapStateToProps, mapDispatchToProps)(InvoiceTable);
